import { useMemo, useState } from 'react';
import {
  DependencyForm,
  SearchBar
} from '../../../../../../pages/DashboardNew/components';
import DependencyCard from '../../../../../../shared/molecules/DependencyCard/DependencyCard';
import { DependencyCardData } from '../../../../../../shared/molecules/DependencyCard/DependencyCard.def';
import { StrategyMapDependency } from '../../../../../StrategyMapDependencyMapper/StrategyMapDependencyMapper';

const DependencyCardSkeleton = () => (
  <div className="mb-4 rounded-xl border border-light-border-default dark:border-slate-700 bg-light-surfacePrimary dark:bg-secondary-dark shadow-sm p-0 overflow-hidden transition-all duration-300 animate-pulse">
    <div className="flex items-center px-4 pt-3 pb-1">
      <span className="w-5 text-base mr-3 flex-shrink-0 text-center">
        <div className="w-2 h-2 bg-light-surfaceSecondary dark:bg-gray-600 rounded-full mx-auto" />
      </span>
      <div className="flex-1 truncate">
        <div className="h-4 bg-light-surfaceSecondary dark:bg-gray-600 rounded w-2/3" />
      </div>
      <div className="ml-2 w-[18px] h-[18px] bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
    </div>
    <div className="flex items-center gap-2 px-4 pb-2">
      <div className="w-4 h-4 bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
      <div className="px-2 py-1 bg-light-surfaceSecondary dark:bg-gray-700 rounded-full">
        <div className="h-3 bg-light-surfaceActive dark:bg-gray-700 rounded w-16" />
      </div>
      <div className="ml-auto flex items-center gap-2">
        <div className="p-1.5 rounded-md">
          <div className="w-[14px] h-[14px] bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
        </div>
        <div className="p-1.5 rounded-md">
          <div className="w-[14px] h-[14px] bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
        </div>
      </div>
    </div>
  </div>
);

const SkeletonList = () => {
    // Hardcoded for dependency type as we only use this for dependencies here
    const skeletonCount = 4;
    return (
        <div className="px-3 mt-1 py-3">
            <div>
            {Array.from({ length: skeletonCount }, (_, index) => (
                <DependencyCardSkeleton key={index} />
            ))}
            </div>
        </div>
    );
};

interface TaskDependencyTabProps {
  taskDependencyManagement: any;
  tasks?: any[];
  readOnly?: boolean;
  simulation?: boolean;
  childEntityType?: string;
  isTasksLoading?: boolean;
  onEntityClick: (id: string) => void;
  currentEntityId?: string;
}

const TaskDependencyTab = ({
  taskDependencyManagement,
  tasks = [], // This should be taskDependenciesState
  readOnly = false,
  simulation = false,
  childEntityType = "Task",
  isTasksLoading = false,
  onEntityClick,
  currentEntityId
}: TaskDependencyTabProps) => {
  const [depSearch, setDepSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

   const allDependencies = useMemo(() => {
     if (!tasks) return [];
     const deps: StrategyMapDependency[] = [];
     tasks.forEach((project: any) => {
      console.log("project", project);
       if (project.dependencies && project.dependencies.length > 0) {
         deps.push({
           id: project.id,
           type: project.type,
           name: project.name || "Unknown",
           _uid: project.id + Math.random().toString(36).substr(2, 9),
           dependency: (project.dependencies || []).map((depItem: any) => {
             const targetProject = tasks.find(
               (p: any) => p.id === depItem.id,
             );
             return {
               id: depItem.id,
               type: depItem.type,
               name: targetProject?.project_name || "Unknown",
             };
           }),
         });
       }
     });
     return deps;
   }, [tasks]);

  //  console.log("allDependencies", allDependencies);

  const handleCancel = ()=>{
     taskDependencyManagement.setEditTaskDep(null)
  }
  // Adapter for DependencyForm to work with DependencyCard's EditorComponent prop
  const EditorAdapter = ({ onCancel, dependency }: { onCancel: () => void, dependency?: { selectedSource: string; selectedDependencies: string[] } }) => {
    
    // Transform tasks/options to StrategyMapDependencyMapper data format
    const taskOptions = taskDependencyManagement.getTaskList();
   
    const dataForMapper = taskOptions.map(
      (opt: any) => ({
        id: opt.value,
        name: opt.label,
        type: opt.type || "task",
      }),
    );

    return (
      <DependencyForm
        isEditing={true}
        isLoading={taskDependencyManagement.isTaskActionLoading}
        selectedProject={taskDependencyManagement.editTaskDepProject}
        dependsOn={taskDependencyManagement.editTaskDepDependsOn}
        projectOptions={taskDependencyManagement.getTaskList()}
        dependencyOptions={taskDependencyManagement.getFilterTaskList(
          taskDependencyManagement.editTaskDepProject,
        )}
        onProjectChange={taskDependencyManagement.setEditTaskDepProject}
        onDependsOnChange={taskDependencyManagement.setEditTaskDepDependsOn}
        onSave={taskDependencyManagement.handleUpdateTaskDependency}
        onCancel={() => {
          handleCancel();
          onCancel();
        }}
        childEntityType={childEntityType}
      />
    );
  };

  console.log("Current expanded id", expandedId)

  return (
    <>
      <div className="flex-1 flex flex-col bg-light-surfacePrimary dark:bg-secondary-dark transition-colors duration-300">
        <div className="depencyTab-header">
          <div className="depencyTab-search-wrapper">
            <SearchBar
              searchValue={depSearch}
              onSearchChange={setDepSearch}
              placeholder={`Search for dependencies`}
              statusFilters={[]}
              setStatusFilters={() => {}}
              statusDropdownOpen={false}
              setStatusDropdownOpen={() => {}}
              showStatusFilter={false}
            />
          </div>
          {!readOnly && (
            <button
              className="h-[40px]  px-3 text-xs font-medium bg-light-surfaceSecondary dark:bg-slate-700 dark:hover:bg-slate-600 text-light-text-primary dark:text-white rounded-xl  transition-colors duration-200 whitespace-nowrap hover:bg-light-interactive-hover"
              onClick={() => {
                taskDependencyManagement.setIsAddingTaskDep(true);
                taskDependencyManagement.setAddTaskDepProject("");
                taskDependencyManagement.setAddTaskDepDependsOn([]);
                setExpandedId(null);
                taskDependencyManagement.setEditTaskDep(null);
              }}
            >
              + Add
            </button>
          )}
        </div>

        <div className="flex-1">
          {isTasksLoading ? (
            <SkeletonList />
          ) : (
            <>
              {!taskDependencyManagement.isAddingTaskDep ? (
                <div className="px-3 mt-1 py-3">
                  {/* Show task dependencies */}
                  {taskDependencyManagement.taskDependenciesState.length > 0 &&
                    !taskDependencyManagement.isAddingTaskDep &&
                    taskDependencyManagement.taskDependenciesState
                    .filter(
                      (dep: any) =>
                        (dep.task_name || "")
                          .toLowerCase()
                          .includes(depSearch.toLowerCase()) ||
                        dep.dependencies.some((d: any) =>
                          (d.task_name || d.label || "")
                            .toLowerCase()
                            .includes(depSearch.toLowerCase()),
                        ),
                    )
                    .map((dep: any, idx: number) => {
                      const isExpanded = expandedId === dep.task_id;

                      // Map to DependencyCardData
                      const cardData: DependencyCardData = {
                        id: dep.id,
                        type: "task",
                        name: dep.task_name,
                        dependency: (dep.dependencies ?? []).map((d: any) => ({
                          id: d.task_id || d.value || d.to || d.id,
                          type: "task", // Assuming task dependencies are always tasks
                          name: d.task_name || d.label || "Unknown",
                          entity_type: "Task",
                        })),
                        entity_type: "Task",
                      };

                      return (
                        <DependencyCard
                          key={dep.task_id}
                          dependency={cardData}
                          onRemove={() => {
                            if (!readOnly) {
                              taskDependencyManagement.handleDeleteAllTaskDependencies(
                                dep.task_id,
                              );
                            }
                          }}
                          onExpanded={(open) => {
                            if (!open) {
                              onEntityClick(
                                `task__${currentEntityId}__${dep.task_id}`,
                              );
                            } else {
                              onEntityClick("");
                            }
                            setExpandedId(!open ? dep.task_id : null);
                          }}
                          expanded={isExpanded}
                          onEdit={() => {
                            if (readOnly) return;

                            setExpandedId(dep.task_id);
                            taskDependencyManagement.setEditTaskDep({
                              depId: dep.task_id,
                              linkIdx: idx,
                            });
                            taskDependencyManagement.setEditTaskDepProject(
                              dep.task_id,
                            );
                            taskDependencyManagement.setEditTaskDepDependsOn(
                              dep.dependencies.map(
                                (d: any) =>
                                  d.task_id || d.value || d.to || d.id,
                              ),
                            );
                            taskDependencyManagement.setIsAddingTaskDep(false);
                          }}
                          entityType="Task"
                          EditorComponent={EditorAdapter}
                        />
                      );
                    })}
              </div>

                  ): !readOnly && (
                <DependencyForm
                  isEditing={false}
                  isLoading={taskDependencyManagement.isTaskActionLoading}
                  selectedProject={taskDependencyManagement.addTaskDepProject}
                  dependsOn={taskDependencyManagement.addTaskDepDependsOn}
                  projectOptions={taskDependencyManagement.getTaskList()}
                  dependencyOptions={taskDependencyManagement.getFilterTaskList(
                    taskDependencyManagement.addTaskDepProject,
                  )}
                  onProjectChange={
                    taskDependencyManagement.setAddTaskDepProject
                  }
                  onDependsOnChange={
                    taskDependencyManagement.setAddTaskDepDependsOn
                  }
                  onSave={taskDependencyManagement.handleAddTaskDependency}
                  onCancel={() =>
                    taskDependencyManagement.setIsAddingTaskDep(false)
                  }
                  childEntityType={childEntityType}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TaskDependencyTab;
